import type { ColumnDef } from '@tanstack/react-table';
import { createElement } from 'react';
import { BucketBadge, StatusBadge } from '@/components/ui/StatusBadge';
import type { ApprovalRequest } from '@/lib/types';
import type { APPayload, APSummary } from './types';
import { formatDate, formatPKR } from './format';
import { humanizeBucket } from './labels';
import { isDuplicateHistory } from './history';
import { compactCsv } from './grnCandidates';

type APRequest = ApprovalRequest<APPayload, APSummary>;

const EM_DASH = '—';

// --- Defensive accessors -----------------------------------------------------
//
// These mirror the v1.1 Wave 0 contract §2.1 fallback chains EXACTLY. Migrated
// rows speak the Retool/Gemini dialect (payload.supplier, payload.po_number)
// while new canonical rows speak the Pydantic dialect (payload.vendor,
// payload.po_no). Every accessor here is null-safe — `summary` and `payload`
// are typed Required on the model but defensive coverage protects against
// accidental empty rows shipped by the API.

function getPayload(req: ApprovalRequest): Record<string, unknown> {
  return (req.payload as Record<string, unknown> | null | undefined) ?? {};
}

function getSummary(req: ApprovalRequest): Record<string, unknown> {
  return (req.summary as Record<string, unknown> | null | undefined) ?? {};
}

function getDefaultFields(req: ApprovalRequest): Record<string, unknown> {
  return readRecord(getPayload(req)['default_fields']);
}

function getStructuredFields(req: ApprovalRequest): Record<string, unknown> {
  return readRecord(getPayload(req)['structured_fields']);
}

function readString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function readCandidateRows(req: ApprovalRequest): Record<string, unknown>[] {
  const payload = getPayload(req);
  const structured = getStructuredFields(req);
  const sources = [
    structured['grn_candidates_selected'],
    structured['postable_grn_candidates'],
    structured['unapproved_grn_candidates'],
    structured['grn_candidates'],
    payload['grn_candidates_selected'],
    payload['grn_candidates'],
  ];
  return sources
    .flatMap((source) => (Array.isArray(source) ? source : []))
    .map(readRecord)
    .filter((candidate) => Object.keys(candidate).length > 0);
}

function hasExtractedPoFallback(req: ApprovalRequest): boolean {
  const structured = getStructuredFields(req);
  const refresh = readRecord(structured['grn_candidate_refresh']);
  return (
    ['GRN_NOT_FOUND', 'GRN_UNAPPROVED'].includes(
      String(req.bucket ?? '').toUpperCase(),
    ) && refresh['fallback'] === 'extracted_po_no_rows_supplier_date'
  );
}

function uniqueCandidateCsv(req: ApprovalRequest, keys: string[]): string | null {
  const values = readCandidateRows(req).flatMap((candidate) =>
    keys.flatMap((key) => String(candidate[key] ?? '').split(',')),
  );
  const unique = [
    ...new Set(values.map((value) => value.trim()).filter(Boolean)),
  ];
  return unique.length === 1 ? unique[0] : null;
}

function vendor(req: ApprovalRequest): string {
  const summary = getSummary(req);
  const payload = getPayload(req);
  const defaults = getDefaultFields(req);
  const gemini = (payload['gemini_raw'] as Record<string, unknown> | undefined) ?? {};
  return (
    readString(summary['vendor']) ??
    readString(defaults['vendor']) ??
    readString(payload['supplier']) ??
    readString(payload['vendor']) ??
    readString(gemini['supplier']) ??
    EM_DASH
  );
}

function invoiceNo(req: ApprovalRequest): string {
  const summary = getSummary(req);
  const payload = getPayload(req);
  const defaults = getDefaultFields(req);
  return (
    readString(summary['invoice_no']) ??
    readString(defaults['invoice_no']) ??
    readString(payload['invoice_no']) ??
    EM_DASH
  );
}

function invoiceDate(req: ApprovalRequest): string {
  const summary = getSummary(req);
  const payload = getPayload(req);
  const defaults = getDefaultFields(req);
  const raw =
    readString(summary['invoice_date']) ??
    readString(defaults['invoice_date']) ??
    readString(payload['invoice_date']);
  return raw ? formatDate(raw) : EM_DASH;
}

function poNo(req: ApprovalRequest): string {
  if (hasExtractedPoFallback(req)) return EM_DASH;

  const summary = getSummary(req);
  const payload = getPayload(req);
  const defaults = getDefaultFields(req);
  const extra = (payload['extra'] as Record<string, unknown> | undefined) ?? {};
  const oracleResult = {
    ...(((extra['result'] as Record<string, unknown> | undefined) ?? {})),
    ...(((payload['oracle_result'] as Record<string, unknown> | undefined) ?? {})),
  };
  return (
    compactCsv(summary['po_no']) ??
    compactCsv(defaults['po_no']) ??
    compactCsv(payload['po_number']) ??
    compactCsv(payload['po_no']) ??
    compactCsv(payload['order_nos']) ??
    compactCsv(payload['ORDER_NOS']) ??
    compactCsv(oracleResult['po_number']) ??
    compactCsv(oracleResult['po_no']) ??
    compactCsv(oracleResult['oracle_order_nos']) ??
    compactCsv(oracleResult['oracle_order_no']) ??
    compactCsv(oracleResult['order_nos']) ??
    compactCsv(oracleResult['ORDER_NOS']) ??
    uniqueCandidateCsv(req, ['po_no', 'PO_NO', 'order_no', 'ORDER_NO', 'order_nos', 'ORDER_NOS']) ??
    EM_DASH
  );
}

function grnNo(req: ApprovalRequest): string {
  const summary = getSummary(req);
  const payload = getPayload(req);
  const defaults = getDefaultFields(req);
  const structured = getStructuredFields(req);
  const structuredOracleResult = readRecord(structured['oracle_result']);
  const extra = readRecord(payload['extra']);
  const extraResult = readRecord(extra['result']);
  const oracleResult = {
    ...extraResult,
    ...readRecord(payload['oracle_result']),
    ...structuredOracleResult,
  };
  return (
    compactCsv(summary['grn_no']) ??
    compactCsv(defaults['grn_no']) ??
    compactCsv(payload['grn_no']) ??
    compactCsv(payload['GRN_NO']) ??
    compactCsv(oracleResult['grn_no']) ??
    compactCsv(oracleResult['GRN_NO']) ??
    compactCsv(oracleResult['grn_nos']) ??
    compactCsv(oracleResult['GRN_NOS']) ??
    uniqueCandidateCsv(req, ['grn_no', 'GRN_NO']) ??
    EM_DASH
  );
}

function grossAmount(req: ApprovalRequest): string {
  const summary = getSummary(req);
  const payload = getPayload(req);
  const defaults = getDefaultFields(req);
  const raw =
    summary['gross_amount'] ??
    defaults['gross_amount'] ??
    payload['gross_amount'] ??
    payload['amount'];
  if (raw === null || raw === undefined || raw === '') return EM_DASH;
  const formatted = formatPKR(raw);
  // formatPKR already handles non-numeric -> em-dash internally.
  return formatted;
}

function historyStatusLabel(req: ApprovalRequest): string {
  if (isDuplicateHistory(req)) return 'Duplicate';
  if (req.status === 'posted') return 'Posted';
  if (req.status === 'rejected') return 'Rejected';
  return humanizeBucket(req.status);
}

function processedBy(req: ApprovalRequest): string {
  const payload = getPayload(req);
  const extra = (payload['extra'] as Record<string, unknown> | undefined) ?? {};
  const oracleResult = {
    ...(((extra['result'] as Record<string, unknown> | undefined) ?? {})),
    ...(((payload['oracle_result'] as Record<string, unknown> | undefined) ?? {})),
  };
  const neededIntervention = oracleResult['needed_intervention'] ?? payload['needed_intervention'];
  const correctedBy =
    readString(payload['posted_by']) ??
    readString(payload['corrected_by']) ??
    readString(payload['approved_by']) ??
    readString(oracleResult['posted_by']) ??
    readString(oracleResult['corrected_by']) ??
    readString(oracleResult['approved_by']);
  if (correctedBy) return correctedBy;
  const processedBy = readString(payload['processed_by']) ?? readString(payload['rejected_by']);
  if (processedBy) return processedBy;
  if (req.status === 'posted' && neededIntervention !== true) return 'N8N';
  return '';
}

function rowType(req: ApprovalRequest): 'service' | 'grn' {
  if (req.bucket === 'POSTED_SERVICE') return 'service';
  const summary = getSummary(req);
  const payload = getPayload(req);
  const structured = readRecord(payload['structured_fields']);
  const errorType = readString(payload['error_type']);
  const oracleClassification = readString(payload['oracle_classification']);
  if (
    (errorType && errorType.includes('GRN')) ||
    (oracleClassification && oracleClassification.includes('GRN')) ||
    Array.isArray(structured['grn_candidates']) ||
    Array.isArray(structured['grn_candidates_selected']) ||
    Array.isArray(structured['postable_grn_candidates']) ||
    Array.isArray(structured['unapproved_grn_candidates'])
  ) {
    return 'grn';
  }
  const summaryType = readString(summary['type']);
  if (summaryType === 'service' || summaryType === 'grn') return summaryType;
  if (
    Object.keys(readRecord(structured['service_preflight'])).length > 0 ||
    Object.keys(readRecord(payload['service_preflight'])).length > 0
  ) {
    return 'service';
  }
  return payload['is_service_invoice'] === true ? 'service' : 'grn';
}

function processedAt(req: ApprovalRequest): string {
  return formatDate(req.resolved_at ?? req.updated_at);
}

// --- Column defs -------------------------------------------------------------

const indexColumn: ColumnDef<ApprovalRequest, unknown> = {
  id: 'index',
  header: '#',
  size: 56,
  enableSorting: false,
  meta: { className: 'sticky left-0 bg-inherit' },
  cell: (info) => info.row.index + 1,
};

const vendorColumn: ColumnDef<ApprovalRequest, unknown> = {
  id: 'vendor',
  header: 'Vendor',
  accessorFn: (row) => vendor(row),
  cell: ({ getValue }) => (getValue() as string) || EM_DASH,
};

const invoiceNoColumn: ColumnDef<ApprovalRequest, unknown> = {
  id: 'invoice_no',
  header: 'Invoice #',
  size: 140,
  accessorFn: (row) => invoiceNo(row),
  cell: ({ getValue }) => (getValue() as string) || EM_DASH,
};

const invoiceDateColumn: ColumnDef<ApprovalRequest, unknown> = {
  id: 'invoice_date',
  header: 'Invoice date',
  size: 132,
  accessorFn: (row) => invoiceDate(row),
  cell: ({ getValue }) => (getValue() as string) || EM_DASH,
};

const poColumn: ColumnDef<ApprovalRequest, unknown> = {
  id: 'po_no',
  header: 'PO #',
  size: 120,
  accessorFn: (row) => poNo(row),
  cell: ({ getValue }) => (getValue() as string) || EM_DASH,
};

const grnColumn: ColumnDef<ApprovalRequest, unknown> = {
  id: 'grn_no',
  header: 'GRN #',
  size: 120,
  accessorFn: (row) => grnNo(row),
  cell: ({ getValue }) => (getValue() as string) || EM_DASH,
};

const amountColumn: ColumnDef<ApprovalRequest, unknown> = {
  id: 'gross_amount',
  header: () => createElement('span', { className: 'block text-right' }, 'Amount'),
  size: 144,
  accessorFn: (row) => {
    const summary = getSummary(row);
    const payload = getPayload(row);
    const defaults = getDefaultFields(row);
    const raw =
      summary['gross_amount'] ??
      defaults['gross_amount'] ??
      payload['gross_amount'] ??
      payload['amount'];
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : 0;
  },
  cell: ({ row }) =>
    createElement(
      'span',
      { className: 'block text-right font-mono tabular-nums' },
      grossAmount(row.original),
    ),
};

const historyStatusColumn: ColumnDef<ApprovalRequest, unknown> = {
  id: 'status',
  header: 'Status',
  size: 128,
  enableSorting: false,
  cell: ({ row }) => createElement(StatusBadge, { status: historyStatusLabel(row.original) }),
};

const bucketColumn: ColumnDef<ApprovalRequest, unknown> = {
  id: 'bucket',
  header: 'Bucket',
  size: 200,
  enableSorting: false,
  cell: ({ row }) =>
    createElement(BucketBadge, { bucket: row.original.bucket ?? 'UNKNOWN' }),
};

const typeColumn: ColumnDef<ApprovalRequest, unknown> = {
  id: 'type',
  header: 'Type',
  size: 96,
  enableSorting: false,
  cell: ({ row }) => {
    const t = rowType(row.original);
    return createElement(
      'span',
      {
        className:
          'inline-flex items-center rounded-full border px-8 py-2 text-xs font-semibold uppercase tracking-wide ' +
          (t === 'service'
            ? 'border-warning-500/30 bg-warning-500/10 text-warning-500'
            : 'border-primary-500/30 bg-primary-500/10 text-primary-600'),
      },
      t === 'service' ? 'Service' : 'GRN',
    );
  },
};

const postedDateColumn: ColumnDef<ApprovalRequest, unknown> = {
  id: 'posted_date',
  header: 'Processed at',
  size: 140,
  accessorFn: (row) => processedAt(row),
  cell: ({ getValue }) => (getValue() as string) || EM_DASH,
};

const processedByColumn: ColumnDef<ApprovalRequest, unknown> = {
  id: 'processed_by',
  header: 'Processed by',
  size: 200,
  enableSorting: false,
  accessorFn: (row) => processedBy(row),
  cell: ({ getValue }) => (getValue() as string) || EM_DASH,
};

// Open queue: BucketBadge already encodes the classification at the precision
// finance needs (POSTED vs DUPLICATE vs SUPPLIER_AMBIGUOUS, etc.), so the
// older `Status` column became a redundant second pill once the bucket
// contract shipped. History keeps its status column because it summarises
// terminal lifecycle (Posted / Rejected / Duplicate) without a bucket pill.
export const COLUMNS_QUEUE: ColumnDef<APRequest, unknown>[] = [
  indexColumn,
  vendorColumn,
  invoiceNoColumn,
  invoiceDateColumn,
  poColumn,
  grnColumn,
  amountColumn,
  bucketColumn,
] as ColumnDef<APRequest, unknown>[];

export const COLUMNS_HISTORY: ColumnDef<APRequest, unknown>[] = [
  indexColumn,
  vendorColumn,
  invoiceNoColumn,
  invoiceDateColumn,
  poColumn,
  grnColumn,
  typeColumn,
  amountColumn,
  historyStatusColumn,
  postedDateColumn,
  processedByColumn,
] as ColumnDef<APRequest, unknown>[];

export const __apColumnTest = {
  historyProcessedDateHeader: () => postedDateColumn.header,
  historyStatusLabel,
  grnNo,
  grossAmount,
  invoiceDate,
  poNo,
  processedAt,
  processedBy,
  rowType,
};
